import React from 'react';
import { OVERLAY_STATE, POSE_TARGETS } from './overlayConfig';
import { getDirection } from './getDirection';

const DIRECTION_TEXT = {
  UP: "Look UP",
  DOWN: "Look DOWN",
  LEFT: "Turn LEFT",
  RIGHT: "Turn RIGHT",
  "UP-LEFT": "Look UP-LEFT",
  "UP-RIGHT": "Look UP-RIGHT",
  "DOWN-LEFT": "Look DOWN-LEFT",
  "DOWN-RIGHT": "Look DOWN-RIGHT",
};

function DirectionalArrow({ dir, color = "#22c55e" }) {
  const pos = {
    UP:          { x: 200, y: 55,  rot: 0    },
    DOWN:        { x: 200, y: 445, rot: 180  },
    LEFT:        { x: 45,  y: 250, rot: -90  },
    RIGHT:       { x: 355, y: 250, rot: 90   },
    "UP-LEFT":   { x: 90,  y: 110, rot: -45  },
    "UP-RIGHT":  { x: 310, y: 110, rot: 45   },
    "DOWN-LEFT": { x: 90,  y: 390, rot: -135 },
    "DOWN-RIGHT":{ x: 310, y: 390, rot: 135  },
  }[dir];

  if (!pos) return null;

  return (
    <g transform={`translate(${pos.x} ${pos.y}) rotate(${pos.rot})`}>
      <path
        d="M -24 8 L 0 -16 L 24 8 L 14 8 L 0 -4 L -14 8 Z"
        fill={color}
        filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.6))"
      />
    </g>
  );
}

function AngleGauge({ pose, target }) {
  if (!target) return null;
  const cx = 200, cy = 475, r = 26;
  const norm = v => Math.max(-1, Math.min(1, v / 35));

  // In sign convention: target is [pitch, yaw]
  // In SVG: x is yaw (positive = left), y is -pitch (positive = up)
  const targetX = cx - norm(target[1]) * r;
  const targetY = cy - norm(target[0]) * r;
  const currentX = cx - norm(pose?.yaw || 0) * r;
  const currentY = cy - norm(pose?.pitch || 0) * r;

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} stroke="#334155" strokeWidth="1.5" fill="rgba(15, 23, 42, 0.8)" />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke="#475569" strokeWidth="1" />
      <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} stroke="#475569" strokeWidth="1" />
      {/* Target dot (amber) */}
      <circle cx={targetX} cy={targetY} r="4" fill="#f59e0b" />
      {/* Current dot (green) */}
      <circle cx={currentX} cy={currentY} r="5" fill="#22c55e" />
    </g>
  );
}

function HeadSilhouette({ angle }) {
  if (!angle) return null;
  const [pitch, yaw] = angle;
  // Tilt visually according to target pitch and yaw
  const transX = -yaw * 1.2;
  const transY = -pitch * 1.2;
  const rotDeg = -yaw * 0.4;
  const transform = `translate(${transX}, ${transY}) rotate(${rotDeg} 200 250)`;

  return (
    <g transform={transform}>
      <ellipse cx="200" cy="250" rx="105" ry="145" stroke="#f59e0b" strokeWidth="2.5" strokeDasharray="6 6" fill="rgba(245, 158, 11, 0.07)" />
      {/* Ghost nose & eye markers */}
      <circle cx="200" cy="250" r="4" fill="#f59e0b" />
      <line x1="165" y1="210" x2="185" y2="210" stroke="#f59e0b" strokeWidth="2" />
      <line x1="215" y1="210" x2="235" y2="210" stroke="#f59e0b" strokeWidth="2" />
    </g>
  );
}

export default function PoseOverlay({
  state = "SEARCHING",
  pose,
  target,
  poseNumber,
  holdProgress = 0,
  qualityWarning = null,
  activePromptText = null
}) {
  const cfg = OVERLAY_STATE[state] ?? OVERLAY_STATE.SEARCHING;
  const direction = getDirection(pose, target, poseNumber);
  const C = cfg.stroke;
  const targetTuple = POSE_TARGETS[poseNumber];

  let displayHint = cfg.hint;
  if (qualityWarning) {
    displayHint = qualityWarning;
  } else if (activePromptText) {
    displayHint = activePromptText;
  } else if (state === "ALIGNING") {
    displayHint = direction ? DIRECTION_TEXT[direction] : (target === "Present_Neutral" ? "Center your face" : "Match the target angle window");
  }

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg
        style={{ width: '100%', height: '100%', maxHeight: '600px' }}
        viewBox="0 0 400 520"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Layer 2: Face oval with state-driven stroke */}
        <ellipse
          cx="200"
          cy="250"
          rx="120"
          ry="160"
          stroke={qualityWarning ? "#ef4444" : C}
          strokeWidth="4"
          fill="none"
          strokeDasharray={cfg.dash}
          style={{ transition: 'stroke 0.2s ease' }}
        />

        {/* Layer 3: Ghost target silhouette (for directional & diagonal poses) */}
        {target && target !== "Present_Neutral" && targetTuple && (
          <g opacity="0.45">
            <HeadSilhouette angle={targetTuple} />
          </g>
        )}

        {/* Layer 4: Hold progress ring */}
        {state === "HOLDING" && (
          <circle
            cx="200"
            cy="250"
            r="175"
            stroke="#22c55e"
            strokeWidth="6"
            fill="none"
            strokeDasharray={`${holdProgress * 1100} 1100`}
            transform="rotate(-90 200 250)"
          />
        )}

        {/* Layer 5: Directional chevron arrow outside the oval */}
        {direction && !qualityWarning && (
          <DirectionalArrow dir={direction} color="#22c55e" />
        )}

        {/* Layer 6: Text hint */}
        <text
          x="200"
          y="445"
          textAnchor="middle"
          fill={qualityWarning ? "#ef4444" : C}
          fontSize="18"
          fontWeight="700"
          fontFamily="system-ui, -apple-system, sans-serif"
          filter="drop-shadow(0px 2px 4px rgba(0,0,0,0.8))"
        >
          {displayHint}
        </text>

        {/* Spatial Angle Gauge (for directional and diagonal poses) */}
        {target && target !== "Present_Neutral" && targetTuple && (
          <AngleGauge pose={pose} target={targetTuple} />
        )}
      </svg>
    </div>
  );
}
